import type { ReactNode } from 'react';
import React, { useRef } from 'react';
import { Icon } from '../Icon';
import styles from './Slider.module.scss';

interface CellSliderProps {
	array: ReactNode[];
	itemWidth: number;
	maxCount: number;
}

const CellSlider = ({ array, itemWidth, maxCount }: CellSliderProps) => {
	const containerReference = useRef<HTMLDivElement>(null);
	const scrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

	const canSlide = array.length > maxCount;
	const visibleWidth = itemWidth * maxCount;

	const onScrollEnd = () => {
		if (!containerReference.current) return;

		const scrollLeft = containerReference.current.scrollLeft;
		const nearestIndex = Math.round(scrollLeft / itemWidth);
		const alignedPosition = nearestIndex * itemWidth;

		containerReference.current.scrollTo({
			behavior: 'smooth',
			left: alignedPosition,
		});
	};

	const onScroll = () => {
		if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
		scrollTimeout.current = setTimeout(() => {
			onScrollEnd();
		}, 50);
	};

	const scrollByItems = (count: number) => {
		if (!containerReference.current) return;

		const maxScrollLeft = containerReference.current.scrollWidth - containerReference.current.clientWidth;
		const current = containerReference.current.scrollLeft;
		const target = Math.min(Math.max(current + count * itemWidth, 0), maxScrollLeft);

		containerReference.current.scrollTo({
			behavior: 'smooth',
			left: target,
		});
	};

	return (
		<div className={styles.cellSliderContainer}>
			{canSlide && (
				<button className={styles.buttonArrowLeft} onClick={() => scrollByItems(-1)}>
					<Icon name='sprite/left-arrow' size={16} color='lightblue-40' />
				</button>
			)}

			<div
				ref={containerReference}
				className={styles.slideContent}
				style={{ maxWidth: visibleWidth }}
				onScroll={onScroll}
			>
				{array.map((item, index) => (
					<div key={`cell-slide ${index + 1}`} className={styles.slideItem} style={{ width: itemWidth }}>
						{item}
					</div>
				))}
			</div>

			{canSlide && (
				<button className={styles.buttonArrowRight} onClick={() => scrollByItems(1)}>
					<Icon name='sprite/right-arrow' size={16} color='lightblue-40' />
				</button>
			)}
		</div>
	);
};

export default CellSlider;
